import { ref, computed } from 'vue'
import { defineStore } from 'pinia'

import { listDatasetLabels } from '@/backend/darwin/listDatasetLabels'
import { useTeamStore } from '@/pinia/useTeamStore'
import { useToast } from '@/uiKit/Toast/useToast'
import { LoadingStatus } from '@/store/types'
import { createDatasetLabel } from '@/backend/darwin/createDatasetLabel'
import { updateDatasetLabel } from '@/backend/darwin/updateDatasetLabel'
import { batchUpdateDatasetLabels } from '@/backend/darwin/batchUpdateDatasetLabels'
import { deleteDatasetLabel } from '@/backend/darwin/deleteDatasetLabel'
import type { DatasetLabelPayload } from '@/store/types/DatasetLabelPayload'

export const useDatasetLabelsStore = defineStore('datasetLabels', () => {
  const teamStore = useTeamStore()
  const toast = useToast()

  const labels = ref<DatasetLabelPayload[]>([])
  const loadingStatus = ref<LoadingStatus>(LoadingStatus.Loading)
  const hasInitialized = ref(false)

  const teamSlug = computed(() => teamStore.currentTeam?.slug ?? '')
  const isLoading = computed(() => loadingStatus.value === LoadingStatus.Loading)
  const isLoaded = computed(() => loadingStatus.value === LoadingStatus.Loaded)

  const shouldShowInitialView = computed(() => {
    if (hasInitialized.value) return false
    if (!isLoaded.value) return true
    return labels.value.length === 0
  })

  function handleError(error: unknown, context: string): never {
    const errorMessage = error instanceof Error ? error.message : `Failed to ${context}`
    toast.error({ meta: { title: errorMessage }, duration: 3000 })

    if (error instanceof Error) {
      throw error
    }

    throw new Error(errorMessage)
  }

  async function loadLabels(): Promise<void> {
    loadingStatus.value = LoadingStatus.Loading

    try {
      const result = await listDatasetLabels(teamSlug.value)

      if (result.ok) {
        labels.value = result.data.sort((a, b) => b.updated_at.localeCompare(a.updated_at))
        loadingStatus.value = LoadingStatus.Loaded
        return
      }

      if ('error' in result) {
        throw new Error(
          typeof result.error.message === 'string'
            ? result.error.message
            : 'Failed to load dataset labels',
        )
      }
    } catch (err: unknown) {
      loadingStatus.value = LoadingStatus.Loaded
      handleError(err, 'load dataset labels')
    }
  }

  async function createLabel(
    name: string,
    color: string,
    datasetIds: number[],
  ): Promise<DatasetLabelPayload | null> {
    try {
      const result = await createDatasetLabel(teamSlug.value, {
        name,
        color,
        datasetIds,
      })

      if (result.ok) {
        labels.value = [result.data, ...labels.value]
        return result.data
      }

      if ('error' in result) {
        throw new Error(
          `Unable to create label "${name}". Check if the name is unique and try again.`,
        )
      }

      return null
    } catch (err: unknown) {
      handleError(err, `create label "${name}"`)
    }
  }

  async function updateLabel(
    id: string,
    data: { name: string; color: string; dataset_ids: number[] },
  ): Promise<void> {
    try {
      const result = await updateDatasetLabel(teamSlug.value, id, data)

      if (result.ok) {
        labels.value = labels.value.map((label) => (label.id === id ? result.data : label))
        return
      }

      if ('error' in result) {
        throw new Error(
          `Unable to update label "${data.name}". Check if the name is unique and try again.`,
        )
      }
    } catch (err: unknown) {
      handleError(err, 'update dataset label')
    }
  }

  async function batchUpdateLabels(
    labelsToUpdate: { id: string; dataset_ids: number[] }[],
  ): Promise<void> {
    try {
      const result = await batchUpdateDatasetLabels(teamSlug.value, {
        dataset_labels: labelsToUpdate,
      })

      if (result.ok) {
        const updatedLabelsMap = new Map(result.data.map((l) => [l.id, l]))

        labels.value = labels.value.map((l) => updatedLabelsMap.get(l.id) || l)
      }

      if ('error' in result) {
        throw new Error(`Unable to update labels. Check if the labels are valid and try again.`)
      }
    } catch (err: unknown) {
      handleError(err, 'batch update dataset labels')
    }
  }

  function getDatasetLabelsToUpdate(
    datasetId: number,
    newLabelIds: string[],
    oldLabelIds: string[],
  ): { id: string; dataset_ids: number[] }[] {
    const newlySelectedLabelIds = newLabelIds.filter((id) => !oldLabelIds.includes(id))
    const unselectedLabelIds = oldLabelIds.filter((id) => !newLabelIds.includes(id))

    if (newlySelectedLabelIds.length === 0 && unselectedLabelIds.length === 0) {
      return []
    }

    const affectedLabels = labels.value.filter(
      (l) => newlySelectedLabelIds.includes(l.id) || unselectedLabelIds.includes(l.id),
    )

    return affectedLabels.map((label) => {
      let updatedDatasetIds = [...label.dataset_ids]

      if (newlySelectedLabelIds.includes(label.id)) {
        updatedDatasetIds.push(datasetId)
      } else if (unselectedLabelIds.includes(label.id)) {
        updatedDatasetIds = updatedDatasetIds.filter((id) => id !== datasetId)
      }

      return {
        id: label.id,
        dataset_ids: [...new Set(updatedDatasetIds)],
      }
    })
  }

  async function deleteLabel(label: DatasetLabelPayload): Promise<void> {
    try {
      const result = await deleteDatasetLabel(teamSlug.value, label.id)

      if ('error' in result) {
        throw new Error(`Unable to delete label. Please try again.`)
      }

      labels.value = labels.value.filter((l) => l.id !== label.id)
      toast.success({
        meta: {
          title: `Label "${label.name}" deleted successfully`,
        },
        duration: 3000,
      })
    } catch (err: unknown) {
      handleError(err, 'delete dataset label')
    }
  }

  function initializeLabels(): void {
    hasInitialized.value = true
  }

  return {
    labels,
    loadingStatus,
    isLoading,
    isLoaded,
    hasInitialized,
    shouldShowInitialView,

    loadLabels,
    createLabel,
    updateLabel,
    batchUpdateLabels,
    getDatasetLabelsToUpdate,
    initializeLabels,
    deleteLabel,
  }
})
